import { useState, useEffect } from 'react';

export default function CompromissoSwish() {
  const [nome, setNome] = useState('');
  const [periodo, setPeriodo] = useState(21);
  const [diasPraticados, setDiasPraticados] = useState<number[]>([]);
  const [assinado, setAssinado] = useState(false);

  useEffect(() => {
    // Carregar do localStorage
    const saved = localStorage.getItem('compromisso-swish');
    if (saved) {
      const data = JSON.parse(saved);
      setNome(data.nome || '');
      setPeriodo(data.periodo || 21);
      setDiasPraticados(data.diasPraticados || []);
      setAssinado(data.assinado || false);
    }
  }, []);

  const salvarCompromisso = () => {
    const data = {
      nome,
      periodo,
      diasPraticados,
      assinado: true,
      dataInicio: new Date().toISOString()
    };
    localStorage.setItem('compromisso-swish', JSON.stringify(data));
    setAssinado(true);
  };

  const toggleDia = (dia: number) => {
    if (diasPraticados.includes(dia)) {
      setDiasPraticados(diasPraticados.filter(d => d !== dia));
    } else {
      setDiasPraticados([...diasPraticados, dia]);
    }
    
    // Salvar automaticamente
    const data = {
      nome,
      periodo,
      diasPraticados: diasPraticados.includes(dia) 
        ? diasPraticados.filter(d => d !== dia)
        : [...diasPraticados, dia],
      assinado
    };
    localStorage.setItem('compromisso-swish', JSON.stringify(data));
  };

  const calcularSequencia = () => {
    if (diasPraticados.length === 0) return 0;
    
    const sorted = [...diasPraticados].sort((a, b) => a - b);
    let sequenciaAtual = 1;
    let maiorSequencia = 1;
    
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === sorted[i - 1] + 1) {
        sequenciaAtual++;
        maiorSequencia = Math.max(maiorSequencia, sequenciaAtual);
      } else {
        sequenciaAtual = 1;
      }
    }
    
    return maiorSequencia;
  };

  const progresso = Math.round((diasPraticados.length / periodo) * 100);
  const sequencia = calcularSequencia();

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Compromisso de Prática
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A repetição é fundamental para instalar novos padrões mentais. Comprometa-se com sua transformação.
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border-2 border-purple-200 shadow-xl">
          {!assinado ? (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Seu Nome Completo
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Digite seu nome..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none text-gray-700"
                />
              </div>

              <div className="bg-white rounded-2xl p-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Período de Prática
                </label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {[7, 14, 21, 30, 60, 90].map((dias) => (
                    <button
                      key={dias}
                      onClick={() => setPeriodo(dias)}
                      className={`px-4 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                        periodo === dias
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {dias} dias
                    </button>
                  ))}
                </div>
                {periodo === 21 && (
                  <p className="text-sm text-purple-600 mt-3 text-center">
                    ⭐ Recomendado: 21 dias para criar um novo hábito
                  </p>
                )}
              </div>

              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-center text-lg">
                  Meu Compromisso
                </h3>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                  <p className="text-gray-700 leading-relaxed text-center">
                    Eu, <strong className="text-purple-600">{nome || '_______________'}</strong>, comprometo-me a praticar a Técnica Swish diariamente por <strong className="text-purple-600">{periodo} dias consecutivos</strong>, dedicando pelo menos 5 minutos por dia para reprogramar meus padrões mentais e criar a vida que desejo.
                  </p>
                </div>
              </div>

              <button
                onClick={salvarCompromisso}
                disabled={!nome.trim()}
                className={`w-full px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 whitespace-nowrap ${
                  nome.trim()
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-xl hover:scale-105'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <i className="ri-quill-pen-line mr-2"></i>
                Assinar Compromisso
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center bg-white rounded-2xl p-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Compromisso Assinado!
                </h3>
                <p className="text-gray-600">
                  Parabéns, <strong>{nome}</strong>! Você se comprometeu com {periodo} dias de prática.
                </p>
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-6 text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">
                    {diasPraticados.length}
                  </div>
                  <div className="text-sm text-gray-600">Dias Praticados</div>
                </div>

                <div className="bg-white rounded-xl p-6 text-center">
                  <div className="text-4xl font-bold text-pink-600 mb-2">
                    {sequencia}
                  </div>
                  <div className="text-sm text-gray-600">Maior Sequência</div>
                </div>

                <div className="bg-white rounded-xl p-6 text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">
                    {progresso}%
                  </div>
                  <div className="text-sm text-gray-600">Progresso</div>
                </div>
              </div>

              {/* Barra de progresso */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Progresso Total</span>
                  <span className="text-sm font-bold text-purple-600">{diasPraticados.length}/{periodo} dias</span>
                </div>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500 rounded-full"
                    style={{ width: `${progresso}%` }}
                  ></div>
                </div>
              </div>

              {/* Calendário */}
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-center">
                  Calendário de Prática
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: periodo }, (_, i) => i + 1).map((dia) => (
                    <button
                      key={dia}
                      onClick={() => toggleDia(dia)}
                      className={`aspect-square rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
                        diasPraticados.includes(dia)
                          ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {dia}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 text-center mt-4">
                  Clique nos dias para marcar como praticado
                </p>
              </div>

              {/* Mensagens motivacionais */}
              {progresso === 100 && (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-trophy-line text-3xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Objetivo Concluído! 🎉
                    </h3>
                    <p className="text-gray-700">
                      Você completou {periodo} dias de prática da Técnica Swish! Seus novos padrões mentais estão instalados. Continue praticando para manter os resultados.
                    </p>
                  </div>
                </div>
              )}

              {sequencia >= 7 && progresso < 100 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-fire-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">
                        Sequência de {sequencia} dias! 🔥
                      </h3>
                      <p className="text-sm text-gray-700">
                        Você está no caminho certo! Continue assim para completar seu objetivo.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}